import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  CircularProgress, 
  Stack, 
  Chip,
  IconButton,
  Divider,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';
import CountdownTimer from '../../components/CountdownTimer/CountdownTimer';
import baseTheme from '../../theme/theme';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import EuroIcon from '@mui/icons-material/Euro';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import ShareIcon from '@mui/icons-material/Share';
import EventIcon from '@mui/icons-material/Event';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';

// Create a custom theme for this page that uses green instead of beige
const courseDetailTheme = createTheme({
  ...baseTheme,
  palette: {
    ...baseTheme.palette,
    primary: {
      main: '#455b40',
      light: '#5a7654',
      dark: '#34442f',
      contrastText: '#FFFFFF'
    }
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(69,91,64,0.2),0px 4px 5px 0px rgba(69,91,64,0.14),0px 1px 10px 0px rgba(69,91,64,0.12)',
    '0px 3px 5px -1px rgba(69,91,64,0.2),0px 5px 8px 0px rgba(69,91,64,0.14),0px 1px 14px 0px rgba(69,91,64,0.12)',
    '0px 3px 5px -1px rgba(69,91,64,0.2),0px 6px 10px 0px rgba(69,91,64,0.14),0px 1px 18px 0px rgba(69,91,64,0.12)',
    '0px 4px 5px -2px rgba(69,91,64,0.2),0px 7px 10px 1px rgba(69,91,64,0.14),0px 2px 16px 1px rgba(69,91,64,0.12)',
    '0px 5px 5px -3px rgba(69,91,64,0.2),0px 8px 10px 1px rgba(69,91,64,0.14),0px 3px 14px 2px rgba(69,91,64,0.12)',
    '0px 5px 6px -3px rgba(69,91,64,0.2),0px 9px 12px 1px rgba(69,91,64,0.14),0px 3px 16px 2px rgba(69,91,64,0.12)',
    '0px 6px 6px -3px rgba(69,91,64,0.2),0px 10px 14px 1px rgba(69,91,64,0.14),0px 4px 18px 3px rgba(69,91,64,0.12)',
    '0px 6px 7px -4px rgba(69,91,64,0.2),0px 11px 15px 1px rgba(69,91,64,0.14),0px 4px 20px 3px rgba(69,91,64,0.12)',
    '0px 7px 8px -4px rgba(69,91,64,0.2),0px 12px 17px 2px rgba(69,91,64,0.14),0px 5px 22px 4px rgba(69,91,64,0.12)',
    '0px 7px 8px -4px rgba(69,91,64,0.2),0px 13px 19px 2px rgba(69,91,64,0.14),0px 5px 24px 4px rgba(69,91,64,0.12)',
    '0px 7px 9px -4px rgba(69,91,64,0.2),0px 14px 21px 2px rgba(69,91,64,0.14),0px 5px 26px 4px rgba(69,91,64,0.12)',
    '0px 8px 9px -5px rgba(69,91,64,0.2),0px 15px 22px 2px rgba(69,91,64,0.14),0px 6px 28px 5px rgba(69,91,64,0.12)',
    '0px 8px 10px -5px rgba(69,91,64,0.2),0px 16px 24px 2px rgba(69,91,64,0.14),0px 6px 30px 5px rgba(69,91,64,0.12)',
    '0px 8px 11px -5px rgba(69,91,64,0.2),0px 17px 26px 2px rgba(69,91,64,0.14),0px 6px 32px 5px rgba(69,91,64,0.12)',
    '0px 9px 11px -5px rgba(69,91,64,0.2),0px 18px 28px 2px rgba(69,91,64,0.14),0px 7px 34px 6px rgba(69,91,64,0.12)',
    '0px 9px 12px -6px rgba(69,91,64,0.2),0px 19px 29px 2px rgba(69,91,64,0.14),0px 7px 36px 6px rgba(69,91,64,0.12)',
    '0px 10px 13px -6px rgba(69,91,64,0.2),0px 20px 31px 3px rgba(69,91,64,0.14),0px 8px 38px 7px rgba(69,91,64,0.12)',
    '0px 10px 13px -6px rgba(69,91,64,0.2),0px 21px 33px 3px rgba(69,91,64,0.14),0px 8px 40px 7px rgba(69,91,64,0.12)',
    '0px 10px 14px -6px rgba(69,91,64,0.2),0px 22px 35px 3px rgba(69,91,64,0.14),0px 8px 42px 7px rgba(69,91,64,0.12)',
    '0px 11px 14px -7px rgba(69,91,64,0.2),0px 23px 36px 3px rgba(69,91,64,0.14),0px 9px 44px 8px rgba(69,91,64,0.12)',
    '0px 11px 15px -7px rgba(69,91,64,0.2),0px 24px 38px 3px rgba(69,91,64,0.14),0px 9px 46px 8px rgba(69,91,64,0.12)'
  ] as any,
  components: {
    ...baseTheme.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(69, 91, 64, 0.1)'
        }
      }
    }
  }
});

interface Course {
  id: number;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  acf?: {
    [key: string]: any;
    day_1_date?: string;
    day_1_time?: string;
    day_1_topic?: string;
    day_2_date?: string;
    day_2_time?: string;
    day_2_topic?: string;
    day_3_date?: string;
    day_3_time?: string;
    day_3_topic?: string;
    count_down_start_date?: string;
    count_down_start_time?: string;
    price?: string;
    location?: string;
    facilitator?: string;
    instructor_name?: string;
  };
}

interface Instructor {
  name: string;
  title: string;
  bio: string;
  image: string;
  experience: string;
  specialties: string[];
}

const CourseDetailSection = styled(Box)(({ theme }) => ({
  padding: '60px 0 80px',
  backgroundColor: '#f8f9fa',
  minHeight: '600px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '300px',
    background: 'linear-gradient(180deg, rgba(69, 91, 64, 0.02) 0%, transparent 100%)',
    pointerEvents: 'none',
  },
  [theme.breakpoints.down('md')]: {
    padding: '40px 0 60px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 0 40px',
  },
}));

const ContentGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 380px',
  gap: '50px',
  width: '100%',
  maxWidth: '1280px',
  margin: '0 auto',
  justifyContent: 'center',
  padding: '0 20px',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr 320px',
    gap: '40px',
    maxWidth: '1100px',
    padding: '0 16px',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '32px',
    maxWidth: '800px',
    padding: '0 16px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '24px',
    padding: '0 12px',
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '40px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  marginBottom: '40px',
  [theme.breakpoints.down('md')]: {
    padding: '32px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px',
  },
  '& h1': {
    fontFamily: '"Lora", serif',
    fontWeight: 700,
    fontSize: '2.5rem',
    marginTop: '32px',
    marginBottom: '20px',
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    [theme.breakpoints.down('md')]: {
      fontSize: '2.2rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  '& h2': {
    fontFamily: '"Lora", serif',
    fontWeight: 700,
    fontSize: '2rem',
    marginTop: '32px',
    marginBottom: '18px',
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.8rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.6rem',
    },
  },
  '& h3': {
    fontFamily: '"Lora", serif',
    fontWeight: 700,
    fontSize: '1.6rem',
    marginTop: '28px',
    marginBottom: '16px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.4rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.3rem',
    },
  },
  '& h4': {
    fontFamily: '"Lora", serif',
    fontWeight: 700,
    fontSize: '1.3rem',
    marginTop: '24px',
    marginBottom: '14px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  '& h5': {
    fontFamily: '"Lora", serif',
    fontWeight: 700,
    fontSize: '1.1rem',
    marginTop: '24px',
    marginBottom: '12px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  '& h6': {
    fontFamily: '"Lora", serif',
    fontWeight: 600,
    fontSize: '1rem',
    marginTop: '20px',
    marginBottom: '10px',
    color: theme.palette.text.primary,
    lineHeight: 1.5,
  },
  '& p': {
    fontFamily: '"Lora", serif',
    fontSize: '16px',
    lineHeight: 1.8,
    marginBottom: '16px',
    color: theme.palette.text.secondary,
  },
  '& ul, & ol': {
    fontFamily: '"Lora", serif',
    fontSize: '16px',
    lineHeight: 1.8,
    marginBottom: '16px',
    paddingLeft: '24px',
    color: theme.palette.text.secondary,
    '& li': {
      marginBottom: '8px',
      listStyleType: 'decimal',
    },
  },
  '& ul li': {
    listStyleType: 'disc',
  },
  '& ol li': {
    listStyleType: 'decimal',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '12px',
    margin: '24px 0',
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& .wp-block-button': {
    margin: '16px 0',
    '& .wp-block-button__link': {
      backgroundColor: theme.palette.primary.main,
      color: '#ffffff !important',
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: '20px',
      padding: '8px 20px',
      fontSize: '14px',
      fontWeight: 700,
      fontFamily: '"Lora", serif',
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-block',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      '& *': {
        position: 'relative',
        zIndex: 2,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        backgroundColor: '#FFFFFF',
        transition: 'left 0.4s ease',
        zIndex: 1,
        borderRadius: '20px',
      },
      '&::after': {
        content: 'attr(data-original-text)',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: '14px',
        fontFamily: '"Lora", serif',
        zIndex: 3,
        opacity: 0,
        transition: 'opacity 0.4s ease',
        whiteSpace: 'nowrap',
      },
      '&:hover': {
        borderColor: `${theme.palette.primary.main} !important`,
        '&::before': {
          left: 0,
        },
        '&::after': {
          opacity: 1,
        },
        '& *': {
          opacity: 0,
        },
      },
    },
  },
  '& button': {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '20px',
    padding: '8px 20px',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: '"Lora", serif',
    cursor: 'pointer',
    margin: '16px 0',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '& *': {
      position: 'relative',
      zIndex: 2,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      backgroundColor: '#FFFFFF',
      transition: 'left 0.4s ease',
      zIndex: 1,
      borderRadius: '20px',
    },
    '&::after': {
      content: 'attr(data-original-text)',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: '14px',
      fontFamily: '"Lora", serif',
      zIndex: 3,
      opacity: 0,
      transition: 'opacity 0.4s ease',
      whiteSpace: 'nowrap',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      '&::before': {
        left: 0,
      },
      '&::after': {
        opacity: 1,
      },
      '& *': {
        opacity: 0,
      },
    },
  },
  '& strong': {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: '20px',
    margin: '24px 0',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
}));

const CourseImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '500px',
  backgroundImage: 'url("/course.png")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: '20px',
  marginBottom: '40px',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '150px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
  },
  [theme.breakpoints.down('md')]: {
    height: '400px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '300px',
  },
}));

const CourseTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '42px',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: '30px',
  lineHeight: 1.3,
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}));

const Sidebar = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: '100px',
  height: 'fit-content',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  minWidth: 0, // Prevent overflow on small screens
  [theme.breakpoints.down('md')]: {
    position: 'static', // Remove sticky on mobile for better scrolling
    gap: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '16px',
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid rgba(0, 0, 0, 0.06)`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const InfoCardHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: '16px 24px',
  color: '#ffffff',
}));

const InfoCardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '18px',
  fontWeight: 700,
  color: '#ffffff',
}));

const InfoCardContent = styled(CardContent)(({ theme }) => ({
  padding: '24px',
}));

const DayCard = styled(Box)(({ theme }) => ({
  marginBottom: '20px',
  paddingBottom: '20px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const DayHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '12px',
}));

const DayBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  fontFamily: '"Lora", serif',
  flexShrink: 0,
  marginTop: '2px',
}));

const DayContent = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const DayDate = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '8px',
}));

const DayTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
  '& svg': {
    fontSize: '18px',
    color: theme.palette.primary.main,
  },
  '& span': {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    fontFamily: '"Lora", serif',
  },
}));

const DayTopic = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  fontFamily: '"Lora", serif',
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '16px',
  '&:last-child': {
    marginBottom: 0,
  },
  '& svg': {
    fontSize: '20px',
    color: theme.palette.primary.main,
    marginTop: '2px',
  },
}));

const InfoText = styled(Box)(({ theme }) => ({
  flex: 1,
  '& .label': {
    fontSize: '13px',
    color: theme.palette.text.secondary,
    marginBottom: '4px',
    fontFamily: '"Lora", serif',
  },
  '& .value': {
    fontSize: '15px',
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontFamily: '"Lora", serif',
  },
}));

const LocationCard = styled(InfoCard)(({ theme }) => ({
  '& iframe': {
    width: '100%',
    height: '200px',
    border: 'none',
    borderRadius: '0 0 20px 20px',
  },
}));

const SocialShare = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  justifyContent: 'center',
  paddingTop: '16px',
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light + '20',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
  },
}));

// Book Slider Styles
const BooksSliderContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '380px',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    minHeight: '320px',
  },
}));

const BooksSlider = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  width: '100%',
  position: 'relative',
}));

const BookSlide = styled(Box)(({ theme }) => ({
  minWidth: '220px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 0.5,
  transform: 'scale(0.8)',
  filter: 'blur(1px)',
  '&.active': {
    opacity: 1,
    transform: 'scale(1)',
    filter: 'blur(0)',
  },
  '&.animating': {
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '180px',
  },
}));

const BookCover = styled(Box)(({ theme }) => ({
  width: '220px',
  height: '280px',
  backgroundColor: theme.palette.primary.light + '20',
  borderRadius: '12px',
  marginBottom: theme.spacing(1.5),
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: `2px solid ${theme.palette.primary.light}30`,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 6px 24px rgba(69, 91, 64, 0.3)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '180px',
    height: '230px',
  },
}));

const BookTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontFamily: '"Lora", serif',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.4,
  marginBottom: theme.spacing(1),
}));

const SliderButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  width: '32px',
  height: '32px',
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&.left': {
    left: 0,
  },
  '&.right': {
    right: 0,
  },
}));

const SpecialBookCover = styled(Box)(({ theme }) => ({
  width: '220px',
  height: '280px',
  backgroundColor: '#f8f9fa',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: '0 4px 16px rgba(69, 91, 64, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f0f1f2',
    boxShadow: '0 6px 24px rgba(69, 91, 64, 0.3)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '180px',
    height: '230px',
    padding: theme.spacing(1),
  },
}));

const DownloadButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  fontSize: '16px',
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  marginTop: theme.spacing(2),
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(69, 91, 64, 0.3)',
  },
}));

const EnrollButton = styled(Button)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  padding: '14px 24px',
  fontSize: '16px',
  fontWeight: 700,
  borderRadius: '12px',
  fontFamily: '"Lora", serif',
  marginTop: '20px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(69, 91, 64, 0.3)',
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
}));

// Add new styled components for better content organization
const ContentSection = styled(Box)(({ theme }) => ({
  marginBottom: '60px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '32px',
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: '30px',
  position: 'relative',
  paddingLeft: '20px',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '30px',
    backgroundColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '28px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
  },
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light + '10',
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  padding: '20px',
  borderRadius: '8px',
  marginBottom: '20px',
}));

// Wavy section styles
const WavySection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
  paddingTop: theme.spacing(20),
  paddingBottom: theme.spacing(10),
  paddingLeft: 0,
  paddingRight: 0,
  position: 'relative',
  overflow: 'hidden',
  width: '100vw',
  marginLeft: 'calc(-50vw + 50%)',
  minHeight: '600px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(69, 91, 65, 0.05) 0%, transparent 50%)',
    pointerEvents: 'none'
  },
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(8),
    minHeight: '500px'
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(6),
    minHeight: '400px'
  }
}));

// Wave at the top
const WaveTop = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '120px',
  zIndex: 10,
  overflow: 'hidden',
  '& svg': {
    width: '100%',
    height: '100%',
    display: 'block'
  },
  [theme.breakpoints.down('lg')]: {
    height: '100px'
  },
  [theme.breakpoints.down('md')]: {
    height: '80px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '60px'
  },
  '@media (max-width: 480px)': {
    height: '50px'
  }
}));

const WavySectionContent = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(3),
  alignItems: 'center',
  position: 'relative',
  zIndex: 1,
  minHeight: '400px',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(4),
    textAlign: 'center',
    minHeight: '300px'
  }
}));

const WavySectionText = styled(Box)(({ theme }) => ({
  maxWidth: '600px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '800px',
    margin: '0 auto'
  }
}));

const WavySectionImage = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  height: '100%',
  position: 'relative',
  overflow: 'visible',
  '& img': {
    width: '120%',
    maxWidth: '900px',
    height: 'auto',
    maxHeight: 'none',
    objectFit: 'cover',
    position: 'absolute',
    bottom: '-80px', // Move down to touch section bottom
    right: '-10%',
  },
  [theme.breakpoints.down('lg')]: {
    '& img': {
      width: '110%',
      maxWidth: '800px',
      right: '-5%',
      bottom: '-60px'
    }
  },
  [theme.breakpoints.down('md')]: {
    order: -1,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      maxWidth: '500px',
      maxHeight: '500px',
      position: 'relative',
      bottom: 'auto',
      right: 'auto'
    }
  }
}));

const CourseDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentBookIndex, setCurrentBookIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Mock instructor data
  const instructors: { [key: string]: Instructor } = {
    "JJ Paniagua": {
      name: "JJ Paniagua",
      title: "Especialista en Agricultura Regenerativa",
      bio: "Con más de 15 años de experiencia en agricultura sostenible, JJ Paniagua ha ayudado a cientos de agricultores a transformar sus prácticas agrícolas. Especialista certificado en métodos JADAM y agricultura orgánica.",
      image: "/jj_inst.png",
      experience: "15+ años",
      specialties: ["JADAM", "Agricultura Orgánica", "Compostaje", "Manejo de Plagas Naturales"]
    },
    "Lucho Iglesias": {
      name: "Lucho Iglesias",
      title: "Experto en Permacultura y Sistemas Sostenibles",
      bio: "Ingeniero agrónomo y permaculturista con amplia experiencia internacional. Lucho se especializa en diseños de sistemas alimentarios sostenibles y ha implementado proyectos exitosos en toda Latinoamérica.",
      image: "/iglesias.png",
      experience: "12+ años",
      specialties: ["Permacultura", "Sistemas Alimentarios", "Diseño Sostenible", "Agricultura Regenerativa"]
    }
  };

  // Mock book data
  const recommendedBooks = [
    {
      id: 1,
      title: 'Libro Natural Farming de Cho Han Kyu',
      coverUrl: null, // Special case for first book
      link: 'https://mailchi.mp/b04483a8d97c/libro',
      isSpecial: true
    },
    {
      id: 2,
      title: 'Versión impresa en español (amazon.es)',
      coverUrl: '/libro-JADAM-en-español-242x300.png',
      link: 'https://www.amazon.es/Agricultura-Ecol%C3%B3gica-PESTICIDAS-NATURALES-agricultura-ebook/dp/B08TC3S176?crid=RTYTG06V0OSF&dib=eyJ2IjoiMSJ9.8IeF07v7NRyHd8bHLUKVsv_e3jRwz-I2E9J1D2H6socylgiv_abxDEceXeVPNGWm-IBas9ci4u3T-BbnggfX_UmKHEDBVnx2iFur_Pul9UV8XTztt18O_13wwvFubAa4wOHeHqYJCgUbyc9DhmEiypP4htGkRJPAcxpFnDX9Xj7x02Szq3obj8f_qJjGECoOIZsTcBQyYxXMW_laiSMzNvDg0iDbaei4JeCiOzUzm2LEeAebYOFgdkl6I9sqoqgy64ZhfUeEooxDyqelLjUrZ9mlVWUPo8EGmipdgxH4RO0.74kS3d8j7OjvtzgthjoENmlyxMSs9mwt_KAncTSJH7E&dib_tag=se&keywords=libro+jadam&psr=EY17&qid=1711271363&s=todays-deals&sprefix=libro+jadam,todays-deals,154&sr=1-1&linkCode=sl1&tag=ekkofood0550-21&linkId=efb15c649ed1cc143684e6da9c4be7c6&language=es_ES&ref_=as_li_ss_tl'
    },
    {
      id: 3,
      title: 'Versión impresa en inglés',
      coverUrl: '/61CSVh3mrYL._AC_UF10001000_QL80_-236x300.jpg',
      link: 'https://www.amazon.com/-/es/Youngsang-Cho/dp/8989220203?__mk_es_US=%C3%85M%C3%85%C5%BD%C3%95%C3%91&crid=2JKUU2062MR40&dib=eyJ2IjoiMSJ9.HyEVbx17PCmzYGtAQtWKwPrX1hzwOgMciGIQJTvGZ_6F2etljl_woys3bkt-pqXT0OhzRAg1ZoZyXFpNU2RZYAL8RnTk_TVeS03Kanuv07guyHYUDt8gFcV6yp9P4vTP3_kSXtXS1ebcLjzGwe1C3L13d26g_hhJoSHo3gUDikVPXMDkPcwDfZAizHnz_MTcDPJz5R3kcP7VFDofBVl9ZZJcIudGP5Tjl-hk1XbPBpY.WITuuWXptpP6fvsnOwFjafMfqy7kSQID9iCLCk8KO1g&dib_tag=se&keywords=jadam&qid=1715883399&sprefix=jadam,aps,196&sr=8-1&linkCode=sl1&tag=ekkofood-20&linkId=9f6f2d04d2d846f094ad02eb63094726&language=es_US&ref_=as_li_ss_tl'
    },
    {
      id: 4,
      title: 'Otros libros de JADAM',
      coverUrl: '/51ESd7kdRQL-238x300.jpg',
      link: 'https://www.amazon.es/making-Natural-Pesticide-Ultra-Low-Cost-agriculture/dp/898922036X?_encoding=UTF8&pd_rd_w=OO1M8&content-id=amzn1.sym.180a9a36-01e7-4f71-b53a-239d259091fd&pf_rd_p=180a9a36-01e7-4f71-b53a-239d259091fd&pf_rd_r=257-6537398-6857712&pd_rd_wg=H5nNQ&pd_rd_r=79a00f51-571f-4bad-9a73-74627a17ca7c&linkCode=sl1&tag=ekkofood0550-21&linkId=b626e68d154fd62baab1d18b4a5b4290&language=es_ES&ref_=as_li_ss_tl'
    }
  ];

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        // First fetch by slug to get the course - add same parameters as working pages
        const timestamp = new Date().getTime();
        const response = await fetch(`https://thomass362.sg-host.com/wp-json/wp/v2/cursos?slug=${slug}&acf_format=standard&_=${timestamp}`);
        if (!response.ok) {
          throw new Error('Failed to fetch course');
        }
        const data = await response.json();
        if (data.length === 0) {
          throw new Error('Course not found');
        }
        setCourse(data[0]); // API returns an array when filtering by slug
        
        // Debug: Check if countdown fields exist
        console.log('count_down_start_date:', data[0].acf?.count_down_start_date);
        console.log('count_down_start_time:', data[0].acf?.count_down_start_time);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCourse();
    }
  }, [slug]);

  // Add button text capture effect after content loads
  useEffect(() => {
    if (course) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const buttons = document.querySelectorAll('.wp-block-button__link, button');
        buttons.forEach((button) => {
          const text = button.textContent?.trim();
          if (text) {
            (button as HTMLElement).setAttribute('data-original-text', text + ' →');
          }
        });
      }, 100);
    }
  }, [course]);

  // Function to render custom sections based on ACF data
  const renderCourseOverview = () => {
    if (!course?.acf) return null;
    
    const overview = [];
    
    // Check for custom overview fields
    if (course.acf.course_duration) {
      overview.push({ label: 'Duración', value: course.acf.course_duration, icon: <AccessTimeIcon /> });
    }
    if (course.acf.course_level) {
      overview.push({ label: 'Nivel', value: course.acf.course_level, icon: <EventIcon /> });
    }
    if (course.acf.course_format) {
      overview.push({ label: 'Formato', value: course.acf.course_format, icon: <PersonIcon /> });
    }
    if (course.acf.course_language) {
      overview.push({ label: 'Idioma', value: course.acf.course_language, icon: <AutoStoriesIcon /> });
    }
    
    // Add default overview if we have basic info
    if (overview.length === 0 && dayInfo.length > 0) {
      overview.push({ label: 'Duración', value: `${dayInfo.length} días`, icon: <AccessTimeIcon /> });
      if (course.acf?.facilitator) {
        overview.push({ label: 'Facilitador', value: course.acf.facilitator, icon: <PersonIcon /> });
      }
      if (course.acf?.location) {
        overview.push({ label: 'Modalidad', value: 'Presencial', icon: <LocationOnIcon /> });
      }
      if (course.acf?.price) {
        overview.push({ label: 'Inversión', value: course.acf.price, icon: <EuroIcon /> });
      }
    }
    
    return overview.length > 0 ? overview : null;
  };

  // Function to extract learning objectives from content
  const extractLearningObjectives = () => {
    if (!course?.acf?.learning_objectives) return null;
    return course.acf.learning_objectives.split('\n').filter(obj => obj.trim());
  };

  // Function to extract course modules
  const extractCourseModules = () => {
    if (!course?.acf) return null;
    
    const modules = [];
    let moduleNum = 1;
    
    // Check for module_1, module_2, etc.
    while (course.acf[`module_${moduleNum}_title`] || course.acf[`module_${moduleNum}_content`]) {
      modules.push({
        title: course.acf[`module_${moduleNum}_title`] || `Módulo ${moduleNum}`,
        content: course.acf[`module_${moduleNum}_content`] || '',
      });
      moduleNum++;
    }
    
    return modules.length > 0 ? modules : null;
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Extract day information from ACF fields
  const getDayInfo = () => {
    if (!course?.acf) return [];
    
    const days = [];
    let dayNum = 1;
    
    // Check for day_1, day_2, day_3, etc. dynamically
    while (course.acf[`day_${dayNum}_date`] || course.acf[`day_${dayNum}_time`] || course.acf[`day_${dayNum}_topic`]) {
      days.push({
        number: dayNum,
        date: course.acf[`day_${dayNum}_date`] || '',
        time: course.acf[`day_${dayNum}_time`] || '',
        topic: course.acf[`day_${dayNum}_topic`] || '',
      });
      dayNum++;
    }
    
    return days;
  };

  if (loading) {
    return (
      <>
        <PageHero
          title="Cargando..."
          backgroundImage="/organic_farm.png"
          height="300px"
          overlayOpacity={0.5}
        />
        <CourseDetailSection>
          <Container maxWidth="lg">
            <LoadingContainer>
              <CircularProgress size={60} sx={{ color: 'primary.main' }} />
            </LoadingContainer>
          </Container>
        </CourseDetailSection>
        <Footer />
      </>
    );
  }

  if (error || !course) {
    return (
      <>
        <PageHero
          title="Error"
          subtitle="No se pudo cargar el curso"
          backgroundImage="/organic_farm.png"
          height="300px"
          overlayOpacity={0.5}
        />
        <CourseDetailSection>
          <Container maxWidth="lg">
            <Typography variant="h6" color="error" textAlign="center">
              {error || 'Curso no encontrado'}
            </Typography>
          </Container>
        </CourseDetailSection>
        <Footer />
      </>
    );
  }

  const dayInfo = getDayInfo();

  // Slider functions
  const handlePrevBook = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    setCurrentBookIndex((prev) => 
      prev === 0 ? recommendedBooks.length - 1 : prev - 1
    );
    
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleNextBook = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    setCurrentBookIndex((prev) => 
      prev === recommendedBooks.length - 1 ? 0 : prev + 1
    );
    
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleDotClick = (index: number) => {
    if (isAnimating || index === currentBookIndex) return;
    setIsAnimating(true);
    setCurrentBookIndex(index);
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Get current instructor based on course data
  const getCurrentInstructor = (): Instructor | null => {
    if (!course?.acf?.instructor_name) return null;
    return instructors[course.acf.instructor_name] || null;
  };

  const currentInstructor = getCurrentInstructor();

  return (
    <ThemeProvider theme={courseDetailTheme}>
      <PageHero
        title={stripHtml(course.title.rendered)}
        backgroundImage="/organic_farm.png"
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: 'Inicio', link: '/' },
          { label: 'Cursos', link: '/cursos' },
          { label: stripHtml(course.title.rendered), link: '#' },
        ]}
        height="300px"
        overlayOpacity={0.5}
      />

      <CourseDetailSection>
        <Box>
          <ContentGrid>
            {/* Main Content */}
            <Box>
              <CourseTitle>{stripHtml(course.title.rendered)}</CourseTitle>
              
              {/* Main Content */}
              <MainContent dangerouslySetInnerHTML={{ __html: course.content.rendered }} />
              
              {/* Learning Objectives Section */}
              {extractLearningObjectives() && (
                <ContentSection>
                  <SectionTitle>
                    Objetivos de Aprendizaje
                  </SectionTitle>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '20px'
                  }}>
                    {extractLearningObjectives()?.map((objective, index) => (
                      <HighlightBox key={index}>
                        <Typography sx={{ fontFamily: '"Lora", serif', lineHeight: 1.8 }}>
                          ✓ {objective}
                        </Typography>
                      </HighlightBox>
                    ))}
                  </Box>
                </ContentSection>
              )}

              {/* Course Modules Section */}
              {extractCourseModules() && (
                <ContentSection>
                  <SectionTitle>
                    Módulos del Curso
                  </SectionTitle>
                  <Box sx={{ display: 'grid', gap: '24px' }}>
                    {extractCourseModules()?.map((module, index) => (
                      <Card key={index} sx={{ 
                        padding: '24px',
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}>
                        <Typography variant="h6" sx={{ 
                          fontFamily: '"Lora", serif',
                          fontWeight: 600,
                          marginBottom: '12px',
                          color: 'primary.main'
                        }}>
                          {module.title}
                        </Typography>
                        <Typography sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.8,
                          fontFamily: '"Lora", serif'
                        }}>
                          {module.content}
                        </Typography>
                      </Card>
                    ))}
                  </Box>
                </ContentSection>
              )}
            </Box>

            {/* Sidebar */}
            <Sidebar>
              {/* Course Schedule Card */}
              {dayInfo.length > 0 && (
                <InfoCard>
                  <InfoCardHeader>
                    <InfoCardTitle>PROGRAMA DEL CURSO</InfoCardTitle>
                  </InfoCardHeader>
                  <InfoCardContent>
                    {dayInfo.map((day) => (
                      <DayCard key={day.number}>
                        <DayHeader>
                          <DayBadge>DÍA {day.number}</DayBadge>
                          <DayContent>
                            <DayDate>{day.date}</DayDate>
                            {day.time && (
                              <DayTime>
                                <AccessTimeIcon />
                                <span>{day.time}</span>
                              </DayTime>
                            )}
                            {day.topic && (
                              <DayTopic>{day.topic}</DayTopic>
                            )}
                          </DayContent>
                        </DayHeader>
                      </DayCard>
                    ))}
                  </InfoCardContent>
                </InfoCard>
              )}

              {/* Recommended Books */}
              <InfoCard>
                <InfoCardHeader>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoStoriesIcon sx={{ fontSize: '20px' }} />
                    <InfoCardTitle>LIBROS RECOMENDADOS</InfoCardTitle>
                  </Box>
                </InfoCardHeader>
                <InfoCardContent sx={{ padding: '16px 8px' }}>
                  <BooksSliderContainer>
                    <SliderButton 
                      className="left"
                      onClick={handlePrevBook}
                      size="small"
                    >
                      <ArrowBackIosIcon sx={{ fontSize: '16px' }} />
                    </SliderButton>
                    
                    <BooksSlider>
                      {/* Show 3 books at a time: previous, current, next */}
                      {[-1, 0, 1].map((offset) => {
                        const index = (currentBookIndex + offset + recommendedBooks.length) % recommendedBooks.length;
                        const book = recommendedBooks[index];
                        const isActive = offset === 0;
                        
                        return (
                          <BookSlide 
                            key={`${book.id}-${offset}`}
                            className={`${isActive ? 'active' : ''} ${isAnimating ? 'animating' : ''}`}
                          >
                            {book.isSpecial ? (
                              <>
                                <SpecialBookCover
                                  onClick={() => isActive && window.open(book.link, '_blank')}
                                  sx={{
                                    cursor: isActive ? 'pointer' : 'default',
                                    opacity: isActive ? 1 : 'inherit',
                                  }}
                                >
                                  <Typography 
                                    sx={{ 
                                      fontSize: '24px', 
                                      fontWeight: 700, 
                                      textAlign: 'center',
                                      lineHeight: 1.3,
                                      color: '#2a3a28',
                                      fontFamily: '"Lora", serif',
                                    }}
                                  >
                                    Libro Natural Farming de Cho Han Kyu
                                  </Typography>
                                </SpecialBookCover>
                                {isActive && (
                                  <DownloadButton
                                    onClick={() => window.open(book.link, '_blank')}
                                    size="small"
                                  >
                                    Descárgalo GRATIS aquí
                                  </DownloadButton>
                                )}
                              </>
                            ) : (
                              <>
                                <BookCover
                                  sx={{
                                    backgroundImage: `url(${book.coverUrl})`,
                                    cursor: isActive ? 'pointer' : 'default',
                                  }}
                                  onClick={() => isActive && window.open(book.link, '_blank')}
                                />
                                {isActive && <BookTitle>{book.title}</BookTitle>}
                              </>
                            )}
                          </BookSlide>
                        );
                      })}
                    </BooksSlider>
                    
                    <SliderButton 
                      className="right"
                      onClick={handleNextBook}
                      size="small"
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: '16px' }} />
                    </SliderButton>
                  </BooksSliderContainer>
                </InfoCardContent>
              </InfoCard>

              {/* Course Details Card */}
              <InfoCard>
                <InfoCardHeader>
                  <InfoCardTitle>DETALLES DEL CURSO</InfoCardTitle>
                </InfoCardHeader>
                <InfoCardContent>
                  {course.acf?.price && (
                    <InfoItem>
                      <EuroIcon />
                      <InfoText>
                        <div className="label">Precio</div>
                        <div className="value">{course.acf.price}</div>
                      </InfoText>
                    </InfoItem>
                  )}
                  
                  {course.acf?.facilitator && (
                    <InfoItem>
                      <PersonIcon />
                      <InfoText>
                        <div className="label">Facilitador</div>
                        <div className="value">{course.acf.facilitator}</div>
                      </InfoText>
                    </InfoItem>
                  )}

                  {course.acf?.location && (
                    <InfoItem>
                      <LocationOnIcon />
                      <InfoText>
                        <div className="label">Ubicación</div>
                        <div className="value">{course.acf.location}</div>
                      </InfoText>
                    </InfoItem>
                  )}

                  <EnrollButton onClick={() => navigate('/registro-curso')}>
                    Inscribirse Ahora
                  </EnrollButton>
                </InfoCardContent>
              </InfoCard>

              {/* Location Card */}
              {course.acf?.location && (
                <LocationCard>
                  <InfoCardHeader>
                    <InfoCardTitle>UBICACIÓN DEL EVENTO</InfoCardTitle>
                  </InfoCardHeader>
                  <iframe
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(course.acf.location)}&output=embed`}
                    title="Event Location"
                    loading="lazy"
                  />
                </LocationCard>
              )}
            </Sidebar>
          </ContentGrid>
        </Box>
      </CourseDetailSection>

      {/* Wavy Section - Only show if instructor exists */}
      {currentInstructor && (
        <WavySection>
          {/* Smooth wavy top */}
          <WaveTop>
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path 
                d="M0,120 C150,20 300,120 600,70 C900,20 1050,120 1200,70 L1200,0 L0,0 Z" 
                fill="#f8f9fa"
              />
            </svg>
          </WaveTop>
          
          <Container maxWidth="lg">
            <WavySectionContent>
              <WavySectionText>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#455b41',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    marginBottom: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  Tu Instructor
                </Typography>
                <Typography
                  variant="h3"
                  component="a"
                  href="https://www.ekkofood.com/j-j-paniagua-y-shodo-sasaki-en-la-historia-de-la-agricultura-organica-de-costa-rica-y-mundial/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 700,
                    color: '#2a3a28',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    marginBottom: 1,
                    fontFamily: '"Lora", serif',
                    lineHeight: 1.2,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: '#455b41',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {currentInstructor.name}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#455b41',
                    fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
                    fontWeight: 600,
                    marginBottom: 3,
                    fontFamily: '"Lora", serif'
                  }}
                >
                  {currentInstructor.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a5a5a',
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    lineHeight: 1.7,
                    marginBottom: 3
                  }}
                >
                  {currentInstructor.bio}
                </Typography>
                <Box sx={{ marginBottom: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#455b41',
                      fontWeight: 600,
                      marginBottom: 1
                    }}
                  >
                    Experiencia: {currentInstructor.experience}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentInstructor.specialties.map((specialty, index) => (
                      <Chip
                        key={index}
                        label={specialty}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(69, 91, 65, 0.1)',
                          color: '#455b41',
                          fontSize: '0.8rem'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/registro-curso')}
                  sx={{
                    backgroundColor: '#455b41',
                    color: '#ffffff',
                    padding: '16px 40px',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    borderRadius: '30px',
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(69, 91, 65, 0.2)',
                    '&:hover': {
                      backgroundColor: '#5a7054',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(69, 91, 65, 0.3)'
                    },
                  }}
                >
                  Aprender con {currentInstructor.name.split(' ')[0]}
                </Button>
              </WavySectionText>
              
              <WavySectionImage>
                <img 
                  src={currentInstructor.image} 
                  alt={`Instructor ${currentInstructor.name}`}
                  loading="lazy"
                />
              </WavySectionImage>
            </WavySectionContent>
          </Container>
        </WavySection>
      )}

      {/* Countdown Section */}
      {course.acf?.count_down_start_date && (
        <Box sx={{ 
          backgroundColor: '#ffffff', 
          padding: '80px 0',
        }}>
          <Container maxWidth="lg">
            <ThemeProvider theme={courseDetailTheme}>
              <CountdownTimer 
                targetDate={course.acf.count_down_start_date}
                targetTime={course.acf.count_down_start_time || "00:00 AM"}
                title="TIEMPO HASTA EL DÍA 1"
              />
            </ThemeProvider>
          </Container>
        </Box>
      )}

      <Footer />
    </ThemeProvider>
  );
};

export default CourseDetail;